import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings2, Plus, Check, GripVertical, X } from 'lucide-react';

// Four columns on desktop, collapsing cleanly on smaller screens.
const COLUMNS = 4;
const GRID = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3';

// Tiles are grouped into fixed rows of four so a thin divider can sit between
// each row of the grid (the 16-tile cap is therefore exactly four rows).
function toRows(items) {
  const rows = [];
  for (let i = 0; i < items.length; i += COLUMNS) rows.push(items.slice(i, i + COLUMNS));
  return rows;
}

/**
 * The dashboard's Quick Access tiles.
 *
 * Display mode renders rows of tiles separated by a hairline divider. Edit mode
 * keeps the identical row layout but makes every tile draggable: the tile under
 * the pointer swaps position as you drag across it, so a tile can be dropped
 * into any row (including the middle ones) and stays exactly where it lands.
 */
export default function QuickAccessGrid({
  items, editing, onEditingChange, onReorder, onRemove, onOpenCustomizer, saving, max,
}) {
  // While a drag is in flight the working order lives here, so the tiles shift
  // live as the pointer moves without writing to the server on every hover.
  const [dragKey, setDragKey] = useState(null);
  const [draftKeys, setDraftKeys] = useState(null);

  const itemByKey = new Map(items.map((item) => [item.to, item]));
  const orderedItems = (draftKeys || items.map((i) => i.to))
    .map((key) => itemByKey.get(key))
    .filter(Boolean);

  const handleDragStart = (event, key) => {
    setDragKey(key);
    setDraftKeys(items.map((i) => i.to));
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  };

  const handleDragOver = (event, key) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!dragKey || key === dragKey) return;
    setDraftKeys((prev) => {
      const list = prev || items.map((i) => i.to);
      const from = list.indexOf(dragKey);
      const to = list.indexOf(key);
      if (from === -1 || to === -1 || from === to) return list;
      const next = [...list];
      next.splice(from, 1);
      next.splice(to, 0, dragKey);
      return next;
    });
  };

  // On release the working order is committed once — the parent saves it to
  // this admin's login, so the arrangement survives a refresh.
  const handleDragEnd = () => {
    const original = items.map((i) => i.to);
    const finalKeys = draftKeys;
    const key = dragKey;
    setDragKey(null);
    setDraftKeys(null);
    if (!key || !finalKeys) return;
    const from = original.indexOf(key);
    const to = finalKeys.indexOf(key);
    if (from !== -1 && to !== -1 && from !== to) onReorder(from, to);
  };

  return (
    <section className="px-6 lg:px-10 py-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">QUICK ACCESS</p>
          {editing && (
            <span className="text-white/25 text-[10px] font-bold tracking-widest">
              {items.length}/{max} TILES{saving ? ' · SAVING…' : ''}
            </span>
          )}
        </div>

        {editing ? (
          <div className="flex items-center gap-2">
            <button
              onClick={onOpenCustomizer}
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/60 hover:text-white text-[10px] font-bold tracking-widest px-3 py-2 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> ADD / REMOVE TILES
            </button>
            <button
              onClick={() => onEditingChange(false)}
              className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-2 transition-colors"
            >
              <Check className="w-3.5 h-3.5" /> DONE
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEditingChange(true)}
            className="inline-flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/50 hover:text-primary text-[10px] font-bold tracking-widest px-3 py-2 transition-colors"
          >
            <Settings2 className="w-3.5 h-3.5" /> CUSTOMIZE
          </button>
        )}
      </div>

      {editing && (
        <p className="text-white/30 text-[11px] mb-4">
          Drag a tile anywhere in the grid — including into another row — or use ✕ to remove it. Up to {max} tiles
          ({max / COLUMNS} rows of {COLUMNS}).
        </p>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 py-10 text-center">
          <p className="text-white/30 text-xs font-bold tracking-widest">NO TILES SELECTED</p>
          <button onClick={onOpenCustomizer} className="mt-3 text-primary text-xs font-bold tracking-widest hover:underline">
            ADD TILES →
          </button>
        </div>
      ) : (
        toRows(orderedItems).map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            {rowIndex > 0 && <div className="h-px bg-white/10 my-3" />}
            <div className={GRID}>
              {row.map((item) =>
                editing ? (
                  <div
                    key={item.to}
                    draggable
                    onDragStart={(event) => handleDragStart(event, item.to)}
                    onDragOver={(event) => handleDragOver(event, item.to)}
                    onDragEnd={handleDragEnd}
                    onDrop={(event) => event.preventDefault()}
                    className={`relative flex flex-col gap-2 border bg-[#111115] p-4 cursor-grab active:cursor-grabbing transition-colors ${
                      dragKey === item.to
                        ? 'border-primary shadow-lg shadow-primary/20 opacity-60'
                        : 'border-white/15 hover:border-white/30'
                    }`}
                  >
                    <item.icon className="w-5 h-5 text-primary" />
                    <span className="text-white/70 text-xs font-bold tracking-wider pr-6">
                      {item.label.toUpperCase()}
                    </span>
                    <GripVertical className="w-4 h-4 text-white/20 absolute top-3 right-3" />
                    <button
                      type="button"
                      title="Remove from Quick Access"
                      onClick={() => onRemove(item.to)}
                      className="absolute bottom-2.5 right-2.5 text-white/25 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="group flex flex-col gap-2 border border-white/10 hover:border-primary/50 bg-[#111115] hover:bg-[#151519] p-4 transition-all"
                  >
                    <item.icon className="w-5 h-5 text-primary" />
                    <span className="text-white/70 group-hover:text-white text-xs font-bold tracking-wider">
                      {item.label.toUpperCase()}
                    </span>
                  </Link>
                )
              )}
            </div>
          </React.Fragment>
        ))
      )}
    </section>
  );
}